import Ionicons from 'react-native-vector-icons/Ionicons';
import { useNavigation, useRoute, useIsFocused } from '@react-navigation/native';
import { useCallback, useEffect, useState } from 'react';
import {
    ActivityIndicator,
    ScrollView,
    StyleSheet,
    Text,
    TouchableOpacity,
    View
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';

import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import { useMissions } from '../context/MissionsContext';
import { apiFetch } from '../api/client';
import { getThemeColors } from '../utils/themeStyles';
import ErrorRetry from '../components/ErrorRetry';

// Chapitre tel que renvoyé par GET /me/stories/{id}/chapters.
interface ApiChapter {
  id: string;
  title: string;
  subtitle?: string;
  order: number;
  locked: boolean;
  completed: boolean;
}

interface Chapter {
  id: string;        // id d'histoire (ex. "chapter-1")
  order: number;
  title: string;
  subtitle: string;
  isUnlocked: boolean;
  isCompleted: boolean;
}

export default function ChaptersScreen() {
  const navigation = useNavigation<any>();
  const route = useRoute<any>();
  const isFocused = useIsFocused();
  const { isDarkTheme } = useTheme();
  const { user, getAccessToken } = useAuth();
  const { activeMissions, trialMissions } = useMissions();
  const colors = getThemeColors(isDarkTheme);

  const missionId: number = route.params?.missionId ?? 1;
  const [filterType, setFilterType] = useState<'all' | 'unlocked' | 'locked'>('all');
  const [chapters, setChapters] = useState<Chapter[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const missionTitle =
    activeMissions.find(m => m.id === missionId)?.title
    ?? trialMissions.find(m => m.id === missionId)?.title
    ?? 'Histoire';

  // Chargement DB+blob : GET /me/stories/{missionId}/chapters (locked/completed).
  const load = useCallback(async () => {
    const token = getAccessToken();
    if (!user || !token) { setChapters([]); setError(false); setLoading(false); return; }
    setLoading(true);
    try {
      const data = await apiFetch<{ chapters: ApiChapter[] }>(`/me/stories/${missionId}/chapters`, { token, auth: true });
      setChapters((data?.chapters ?? []).map(c => ({
        id: c.id,
        order: c.order,
        title: c.title,
        subtitle: c.subtitle ?? '',
        isUnlocked: !c.locked,
        isCompleted: c.completed,
      })));
      setError(false);
    } catch (_) {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [user, getAccessToken, missionId]);

  // Recharge à chaque focus (le déblocage évolue après complétion d'un chapitre).
  useEffect(() => { if (isFocused) void load(); }, [isFocused, load]);

  const filteredChapters = filterType === 'all'
    ? chapters
    : filterType === 'unlocked'
    ? chapters.filter(c => c.isUnlocked)
    : chapters.filter(c => !c.isUnlocked);

  const handleChapterPress = (chapter: Chapter) => {
    if (chapter.isUnlocked) {
      navigation.navigate('ChapterDetail' as never, {
        missionId,
        chapterId: chapter.order,     // ordre numérique (compat ChapterDetail)
        storyChapterId: chapter.id,   // id d'histoire pour l'API (gate + complétion)
      } as never);
    }
  };

  const renderFilterTab = (type: 'all' | 'unlocked' | 'locked', label: string, count: number) => (
    <TouchableOpacity
      key={type}
      style={[
        styles.filterTab,
        { backgroundColor: filterType === type ? colors.primary : colors.surfaceSecondary },
      ]}
      onPress={() => setFilterType(type)}
    >
      <Text style={[
        styles.filterTabText,
        { color: filterType === type ? colors.badgeText : colors.textSecondary },
      ]}>
        {label} ({count})
      </Text>
    </TouchableOpacity>
  );

  const renderChapter = (chapter: Chapter) => {
    const borderColor = chapter.isCompleted
      ? colors.success
      : chapter.isUnlocked
      ? colors.primary
      : colors.border;

    return (
      <TouchableOpacity
        key={chapter.id}
        style={[
          styles.chapterCard,
          {
            backgroundColor: colors.cardBackground,
            borderLeftColor: borderColor,
            borderLeftWidth: 4,
            shadowColor: colors.cardShadow,
            opacity: chapter.isUnlocked ? 1 : 0.7,
          },
        ]}
        onPress={() => handleChapterPress(chapter)}
        disabled={!chapter.isUnlocked}
      >
        {/* Header row */}
        <View style={styles.chapterHeader}>
          <View style={styles.chapterTitleRow}>
            <View style={styles.chapterTitleBlock}>
              <Text style={[styles.chapterNum, { color: colors.textSecondary }]}>
                Chapitre {chapter.order < 10 ? `0${chapter.order}` : chapter.order}
              </Text>
              <Text style={[styles.chapterTitle, { color: chapter.isUnlocked ? colors.textPrimary : colors.textSecondary }]}>
                {chapter.title}
              </Text>
              {!!chapter.subtitle && (
                <Text style={[styles.chapterSubtitle, { color: colors.textSecondary }]}>
                  {chapter.subtitle}
                </Text>
              )}
            </View>
            <View style={styles.chapterIcons}>
              {chapter.isCompleted ? (
                <Ionicons name="checkmark-circle" size={22} color={colors.success} />
              ) : chapter.isUnlocked ? (
                <Ionicons name="play-circle" size={22} color={colors.primary} />
              ) : (
                <Ionicons name="lock-closed" size={20} color={colors.textSecondary} />
              )}
            </View>
          </View>
        </View>

        {/* Status row */}
        <View style={styles.lockedRow}>
          {chapter.isCompleted ? (
            <Text style={[styles.unlockText, { color: colors.success, fontStyle: 'normal' }]}>Complété</Text>
          ) : chapter.isUnlocked ? (
            <Text style={[styles.unlockText, { color: colors.primary, fontStyle: 'normal' }]}>Disponible — appuyez pour ouvrir</Text>
          ) : (
            <>
              <Ionicons name="lock-closed-outline" size={13} color={colors.textTertiary} />
              <Text style={[styles.unlockText, { color: colors.textTertiary }]}>
                Terminez le chapitre précédent pour débloquer
              </Text>
            </>
          )}
        </View>
      </TouchableOpacity>
    );
  };

  const counts = {
    all: chapters.length,
    unlocked: chapters.filter(c => c.isUnlocked).length,
    locked: chapters.filter(c => !c.isUnlocked).length,
  };

  return (
    <SafeAreaView style={[styles.container, { backgroundColor: colors.background }]}>
      {/* Header */}
      <View style={[styles.header, { backgroundColor: colors.surface, borderBottomColor: colors.border }]}>
        <View style={styles.headerContent}>
          <TouchableOpacity style={styles.backButton} onPress={() => navigation.goBack()}>
            <Ionicons name="arrow-back" size={24} color={colors.iconPrimary} />
          </TouchableOpacity>
          <View style={styles.headerTextBlock}>
            <Text style={[styles.headerTitle, { color: colors.textPrimary }]} numberOfLines={1}>{missionTitle}</Text>
            <Text style={[styles.headerSubtitle, { color: colors.textSecondary }]}>Chapitres</Text>
          </View>
          <View style={styles.headerRight} />
        </View>
      </View>

      {/* Filter Tabs */}
      <View style={[styles.filtersContainer, { backgroundColor: colors.surface, borderBottomColor: colors.border }]}>
        <View style={styles.filterOptions}>
          {renderFilterTab('all', 'Tous', counts.all)}
          {renderFilterTab('unlocked', 'Disponibles', counts.unlocked)}
          {renderFilterTab('locked', 'Verrouilles', counts.locked)}
        </View>
      </View>

      {/* Chapters List */}
      <ScrollView style={styles.content} showsVerticalScrollIndicator={false}>
        <View style={styles.chaptersList}>
          {error && chapters.length === 0 ? (
            <ErrorRetry onRetry={() => void load()} retrying={loading} message="Impossible de charger les chapitres." />
          ) : loading && chapters.length === 0 ? (
            <View style={styles.emptyState}>
              <ActivityIndicator color={colors.primary} />
            </View>
          ) : filteredChapters.length === 0 ? (
            <View style={styles.emptyState}>
              <Ionicons name="book" size={48} color={colors.textTertiary} />
              <Text style={[styles.emptyTitle, { color: colors.textPrimary }]}>Aucun chapitre</Text>
              <Text style={[styles.emptySubtitle, { color: colors.textSecondary }]}>
                {chapters.length === 0 ? "Cette mission n'a pas encore de chapitres." : 'Modifiez le filtre'}
              </Text>
            </View>
          ) : (
            filteredChapters.map(renderChapter)
          )}
        </View>
      </ScrollView>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1 },
  header: { borderBottomWidth: 1, paddingHorizontal: 20, paddingVertical: 16 },
  headerContent: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' },
  backButton: { padding: 8, marginLeft: -8 },
  headerTextBlock: { flex: 1, alignItems: 'center' },
  headerTitle: { fontSize: 20, fontWeight: 'bold' },
  headerSubtitle: { fontSize: 12, marginTop: 2 },
  headerRight: { width: 40 },
  filtersContainer: { padding: 12, borderBottomWidth: 1 },
  filterOptions: { flexDirection: 'row', gap: 8 },
  filterTab: { paddingHorizontal: 12, paddingVertical: 6, borderRadius: 16 },
  filterTabText: { fontSize: 12, fontWeight: '500' },
  content: { flex: 1 },
  chaptersList: { padding: 16 },
  chapterCard: {
    borderRadius: 12,
    padding: 16,
    marginBottom: 12,
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.1,
    shadowRadius: 3,
    elevation: 2,
  },
  chapterHeader: { marginBottom: 12 },
  chapterTitleRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
  },
  chapterTitleBlock: { flex: 1, marginRight: 8 },
  chapterNum: { fontSize: 11, fontWeight: '600', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 2 },
  chapterTitle: { fontSize: 17, fontWeight: '700', marginBottom: 4 },
  chapterSubtitle: { fontSize: 13, lineHeight: 18 },
  chapterIcons: { alignItems: 'flex-end', gap: 4 },
  tagBadge: { paddingHorizontal: 8, paddingVertical: 3, borderRadius: 8 },
  tagText: { fontSize: 11, fontWeight: '700' },
  progressSection: { marginTop: 4 },
  progressRow: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 },
  progressText: { fontSize: 12, fontWeight: '500' },
  durationText: { fontSize: 12 },
  progressBar: { height: 6, borderRadius: 3, overflow: 'hidden' },
  progressFill: { height: '100%', borderRadius: 3 },
  lockedRow: { flexDirection: 'row', alignItems: 'center', gap: 5, marginTop: 4 },
  unlockText: { fontSize: 12, fontStyle: 'italic' },
  emptyState: { alignItems: 'center', justifyContent: 'center', paddingVertical: 48 },
  emptyTitle: { fontSize: 18, fontWeight: '600', marginTop: 16, marginBottom: 8 },
  emptySubtitle: { fontSize: 14, textAlign: 'center' },
});
